import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { checkSession, clearSession, getUser, updateActivity } from '../../utils/session';
import { AdminOverlaysProvider } from '../admin/overlays';
import { Button, Modal, Spinner, StatusPill } from '../admin/ui';
import SiteCredit from '../SiteCredit';
import { IconClose, IconLink, IconLock, IconLogout, IconMenu } from '../icons';
import { courierNavItems, getCourierPageMeta } from './navigation';

const CourierContext = createContext(null);
export const useCourierSession = () => useContext(CourierContext);

/** This panel's own session only — admin and merchant keep their own. */
const endCourierSession = () => clearSession('courier');

/**
 * Shell for every /courier/* portal route.
 *
 * Like the merchant shell, the guard checks the role rather than merely that a
 * token is valid: an admin or merchant token is perfectly authentic and must
 * still not open the courier portal.
 *
 * The account is loaded here, once, rather than by each page: the nav needs
 * the plan to badge the paid pages, and every page needs it to decide between
 * showing data and offering the upgrade.
 */
const CourierLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [me, setMe] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const performLogout = useCallback(() => {
    endCourierSession();
    navigate('/courier/login', { replace: true });
  }, [navigate]);

  /** Pages call this after anything that changes the plan or the account. */
  const refreshMe = useCallback(
    () =>
      api
        .get('/api/courier-portal/me')
        .then(({ data }) => {
          setMe(data);
          return data;
        })
        .catch(() => null),
    []
  );

  useEffect(() => {
    if (!checkSession('courier')) {
      endCourierSession();
      navigate('/courier/login', { replace: true });
      return undefined;
    }

    const stored = getUser('courier');
    if (!stored || stored.role !== 'courier') {
      endCourierSession();
      navigate('/courier/login', { replace: true });
      return undefined;
    }
    setUser(stored);
    updateActivity('courier');

    let cancelled = false;
    api
      .get('/api/auth/verify')
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.user?.role !== 'courier') {
          endCourierSession();
          navigate('/courier/login', { replace: true });
          return;
        }
        setUser((current) => ({ ...current, ...data.user }));
        refreshMe().finally(() => {
          if (!cancelled) setVerifying(false);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // Only a genuine auth rejection ends the session: a rate-limit
        // response or a restarting API must not look like an expired token.
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          endCourierSession();
          navigate('/courier/login', { replace: true });
          return;
        }
        setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, refreshMe]);

  useEffect(() => {
    const onActivity = () => updateActivity('courier');
    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    return () => {
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (verifying && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div className="flex items-center gap-3 text-sm font-medium text-ink-500">
          <Spinner className="h-5 w-5 text-info-600" />
          Loading your portal…
        </div>
      </div>
    );
  }

  const meta = getCourierPageMeta(location.pathname);
  const fullAccess = Boolean(me?.account?.fullAccess);

  const navLink = ({ isActive }) =>
    `flex items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600 ${
      isActive
        ? 'bg-info-50 font-600 text-info-700'
        : 'font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900'
    }`;

  const navContent = (
    <>
      {courierNavItems.map((item) => (
        <NavLink key={item.to} to={item.to} className={navLink}>
          <span className="flex items-center gap-2">
            <item.icon size={17} />
            {item.label}
          </span>
          {/* Locked pages stay in the nav: the plan has to be visible to be
              worth buying, and the page behind explains what is in it. A
              padlock rather than the word "Pro" — five nav items plus five
              badges overflowed the header into the account controls. */}
          {item.paidOnly && !fullAccess && (
            <IconLock size={12} className="shrink-0 text-ink-400" aria-label="Intelligence plan" />
          )}
        </NavLink>
      ))}
    </>
  );

  return (
    <CourierContext.Provider value={{ user, me, refreshMe, fullAccess }}>
      <AdminOverlaysProvider>
        <div className="min-h-screen bg-ink-50">
          <header className="sticky top-0 z-40 border-b border-ink-200 bg-white">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-20 items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-5">
                  <img src="/brand.png" alt="ParcelBuddy" className="h-11 w-auto shrink-0" />
                  <nav className="hidden items-center gap-1 lg:flex" aria-label="Portal">
                    {navContent}
                  </nav>
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Opens the public site in a new tab"
                    className="hidden whitespace-nowrap items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 2xl:inline-flex"
                  >
                    <IconLink size={15} />
                    View public site
                  </a>
                  <div className="hidden min-w-0 text-right sm:block">
                    <p className="truncate text-sm font-600 leading-tight text-ink-900">{user?.name}</p>
                    <p className="truncate text-xs leading-tight text-ink-500">{me?.account?.name}</p>
                  </div>
                  <Button variant="secondary" size="sm" className="shrink-0" onClick={() => setLogoutOpen(true)}>
                    <IconLogout size={15} />
                    <span className="hidden sm:inline">Sign out</span>
                  </Button>
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen((open) => !open)}
                    aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileNavOpen}
                    className="-mr-1 rounded-lg p-2 text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600 lg:hidden"
                  >
                    {mobileNavOpen ? <IconClose size={20} /> : <IconMenu size={20} />}
                  </button>
                </div>
              </div>
            </div>

            {mobileNavOpen && (
              <div className="border-t border-ink-200 bg-white lg:hidden">
                <nav className="mx-auto w-full max-w-6xl space-y-1 px-4 py-3 sm:px-6" aria-label="Portal mobile">
                  {navContent}
                </nav>
              </div>
            )}
          </header>

          <main>
            <div className="mx-auto w-full max-w-6xl px-4 py-7 sm:px-6 lg:px-8">
              <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="font-display text-2xl font-800 tracking-tight text-ink-900">
                      {meta.title}
                    </h1>
                    {me?.account && (
                      <StatusPill tone={fullAccess ? 'success' : 'info'}>
                        {fullAccess ? 'Intelligence' : 'Pilot'}
                      </StatusPill>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-ink-600">{meta.description}</p>
                </div>
              </div>

              <Outlet />
            </div>
          </main>

          <footer className="border-t border-ink-200 bg-white">
            <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-ink-500 sm:px-6 lg:px-8">
              <p>ParcelBuddy courier portal · © {new Date().getFullYear()}</p>
              <SiteCredit />
            </div>
          </footer>
        </div>

        <Modal
          open={logoutOpen}
          onClose={() => setLogoutOpen(false)}
          title="Sign out"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
                Cancel
              </Button>
              <Button onClick={performLogout}>Sign out</Button>
            </>
          }
        >
          <p className="text-sm leading-relaxed text-ink-600">
            You&apos;ll need to sign in again to see your intelligence dashboard.
          </p>
        </Modal>
      </AdminOverlaysProvider>
    </CourierContext.Provider>
  );
};

export default CourierLayout;
